/**
 * A short list of recently opened records, per user.
 *
 * Record-centric apps live on "the patient I was just looking at", and typing a
 * PIN again to get back to it is friction the palette can remove. Bounded at
 * ten because an unbounded MRU stops being recent.
 */

import { readStored, writeStored } from './storage';

const KEY = 'ndc.recent';
const LIMIT = 10;

export interface RecentItem {
  /** Route to open. Doubles as the identity for de-duplication. */
  href: string;
  title: string;
  subtitle?: string;
}

function storageKey(userId: number | undefined): string {
  return `${KEY}.${userId ?? 'anon'}`;
}

export function readRecent(userId: number | undefined): RecentItem[] {
  const parsed = parseJson(readStored(storageKey(userId)));
  if (!Array.isArray(parsed)) return [];

  // Written by this app, but it is user-editable storage — validate the shape
  // rather than trusting it into the UI.
  return parsed.filter(isRecentItem).slice(0, LIMIT);
}

/** Adds an item, moving an existing entry to the front rather than duplicating it. */
export function pushRecent(userId: number | undefined, item: RecentItem): void {
  const others = readRecent(userId).filter((entry) => entry.href !== item.href);
  writeStored(storageKey(userId), JSON.stringify([item, ...others].slice(0, LIMIT)));
}

/** Null for nothing stored or text that is not JSON — both mean "no recents". */
function parseJson(raw: string | null): unknown {
  if (raw === null) return null;
  try {
    return JSON.parse(raw) as unknown;
  } catch (error: unknown) {
    if (error instanceof SyntaxError) return null;
    throw error;
  }
}

function isRecentItem(value: unknown): value is RecentItem {
  if (typeof value !== 'object' || value === null) return false;
  return (
    'href' in value &&
    typeof value.href === 'string' &&
    'title' in value &&
    typeof value.title === 'string'
  );
}
